import type { ChangeEvent } from 'react';
import type { ContentTypeDefinition } from '../../lib/flotiq-client/types/api-response';
import type { DataGridColumnProps } from '../DataGrid/DataGrid';
import type { GridFilters, GridFiltersChangeFn } from './filterDefinitions';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getTestProps } from '../../lib/helpers';

import useDebounceCallback from '../../hooks/useDebounceCallback';

import {
  FilterListIcon,
  MagnifierIcon,
  RefreshIcon,
} from '../../images/shapes';

import Button from '../Button/Button';
import Input from '../Input/Input';
import PopoverComponent from '../Popover/Popover';
import PopoverItem from '../Popover/PopoverItem/PopoverItem';
import { getDefaultFilterOperator } from './filterDefinitions';
import GridFilter from './GridFilter/GridFilter';

interface GridFiltersManagerProps {
  /**
   * The columns available for filtering.
   */
  columns: DataGridColumnProps[];
  /**
   * The current filters applied to the grid.
   */
  filters: GridFilters;
  /**
   * Callback fired when the filters are changed.
   */
  onFiltersChange: GridFiltersChangeFn;
  /**
   * The current value of the search input.
   */
  searchValue?: string;
  /**
   * Callback fired when the search input value is changed.
   */
  onSearchChange?: (value: string) => void;
  /**
   * Whether to show the search input.
   */
  showSearchInput?: boolean;
  /**
   * Whether to show operator selectors. Disable this for APIs that only
   * support the legacy single-value filter semantics.
   */
  showOperators?: boolean;
  /**
   * The content type of the items being filtered.
   */
  contentType?: ContentTypeDefinition | null;
  /**
   * Callback fired when the grid is reset.
   */
  onResetGrid?: () => void;
  /**
   * The test ID for the component, used for testing purposes.
   */
  testId?: string;
}

const GridFiltersManager = ({
  columns,
  filters,
  onFiltersChange,
  searchValue,
  onSearchChange,
  showSearchInput = false,
  showOperators = false,
  contentType,
  onResetGrid,
  testId = '',
}: GridFiltersManagerProps) => {
  const { t } = useTranslation();

  const availableFilterColumns = useMemo(() => {
    const currentFilterKeys = Object.keys(filters || {});
    return columns.filter(
      (column) =>
        !!column.filterInputType &&
        !currentFilterKeys.includes(column.accessor || ''),
    );
  }, [filters, columns]);

  const columnsByFieldName = columns.reduce<
    Record<string, DataGridColumnProps>
  >((acc, column) => {
    if (column.accessor) {
      acc[column.accessor] = column;
    }
    return acc;
  }, {});

  const handleAddFilter = (field: string) => {
    onFiltersChange({
      ...filters,
      [field]: {
        type: getDefaultFilterOperator(
          columnsByFieldName[field]?.filterInputType,
        ),
        filter: '',
      },
    });
  };

  const debouncedSearchChange = useDebounceCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      onSearchChange?.(event.target.value),
    300,
  );

  return (
    <div
      className="flex w-full shrink-0 flex-wrap items-center gap-2 mb-3"
      aria-label={t('ObjectsOfType.Filters')}
      {...getTestProps(testId, 'filters')}
    >
      {showSearchInput && (
        <div className="relative w-70">
          <MagnifierIcon className="absolute top-2 left-4 z-1 w-4 h-4 text-blue" />
          <Input
            type="search"
            value={searchValue ?? ''}
            placeholder={t('ObjectsOfType.SearchPlaceholder')}
            onChange={debouncedSearchChange}
            additionalInputClasses="h-8 pl-11 placeholder:text-slate-500 dark:bg-slate-800 dark:border-slate-700"
            isSearchIcon={false}
            aria-label={t('ObjectsOfType.SearchPlaceholder')}
            {...(testId ? { 'data-testid': `${testId}-search` } : {})}
          />
        </div>
      )}

      {Object.entries(filters || {}).map(([field, filter], rowIndex) => (
        <GridFilter
          key={`${field}-${rowIndex}`}
          filter={filter}
          fieldName={field}
          column={columnsByFieldName[field]}
          onFiltersChange={onFiltersChange}
          filters={filters}
          showOperators={showOperators}
          contentType={contentType}
          {...getTestProps(testId, `filter-${field}`, 'testId')}
        />
      ))}

      {availableFilterColumns.length > 0 && (
        <PopoverComponent
          popoverPanelAnchorTo="bottom start"
          additionalPopoverPanelClasses="border-1 shadow-none min-w-(--button-width) !max-w-150 z-50 dark:bg-slate-800"
          popoverButton={
            <Button
              as="div"
              iconImage={<FilterListIcon />}
              iconPosition="start"
              buttonColor="slateBordered"
              additionalClasses={
                'border-1 text-slate-700 border-slate-200 ' +
                'dark:border-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700'
              }
              {...getTestProps(testId, 'add-filter', 'testId')}
            >
              {t('ObjectsOfType.AddFilter')}
            </Button>
          }
          content={
            <div {...getTestProps(testId, 'add-filter-menu')}>
              <div className="text-slate-500 h-9 px-3 flex items-center">
                {t('ObjectsOfType.Filters')}
              </div>

              {availableFilterColumns.map((column) => (
                <PopoverItem
                  key={column.accessor}
                  onClick={() => handleAddFilter(column.accessor)}
                  label={column.label}
                  color="black"
                  closePopover
                  additionalClasses={
                    'font-normal hover:no-underline hover:bg-slate-50 dark:hover:bg-slate-700 px-3 h-9 xl:h-9 ' +
                    'min-h-fit whitespace-break-spaces justify-start text-left'
                  }
                  {...getTestProps(
                    testId,
                    `add-filter-option-${column.accessor}`,
                    'testId',
                  )}
                />
              ))}
            </div>
          }
        />
      )}

      {onResetGrid && (
        <Button
          iconImage={<RefreshIcon className="text-blue w-3 md:w-4" />}
          onClick={onResetGrid}
          buttonColor="borderless"
          additionalClasses="ml-auto"
        >
          {t('Global.ResetGrid')}
        </Button>
      )}
    </div>
  );
};

export default GridFiltersManager;
